import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './config';
import { sanitizeFileName } from '@/utils/fileNameSanitizer';

export interface VendorDocument {
  id: string;
  fileName: string;
  fileUrl: string;
  documentType: 'communication' | 'amendment' | 'certificate' | 'license' | 'other';
  uploadedAt: any; // Firestore timestamp
  uploadedBy?: {
    id: string;
    email?: string;
    name?: string;
  };
  description?: string;
  fileSize?: number; // in bytes
}

export interface Vendor {
  id?: string;
  name: string;
  category?: string; // e.g., "Fire Protection", "Elevator", "Maintenance", "Security", "HVAC", etc.
  contact?: string; // Contact person name
  email?: string;
  phone?: string;
  locationIds?: string[]; // Array of location IDs this vendor is associated with
  organizationId?: string; // Multi-tenant support
  supplementaryDocuments?: VendorDocument[]; // Supplementary documents (not contracts)
  deleted?: boolean; // Soft delete flag
  createdAt?: any;
  updatedAt?: any;
}

// Vendor service
export const vendorService = {
  // Get all vendors (excluding deleted ones) for an organization
  getAllVendors: async (organizationId?: string) => {
    try {
      const vendorsRef = collection(db, 'vendors');
      
      // Build query with organization filter if provided
      let q;
      if (organizationId) {
        // Filter by organizationId
        q = query(
          vendorsRef,
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(vendorsRef, orderBy('name'));
      }
      
      const querySnapshot = await getDocs(q);
      const vendors: Vendor[] = [];
      querySnapshot.forEach((doc) => {
        const vendorData = { id: doc.id, ...doc.data() } as Vendor;
        // Filter out deleted vendors
        if (!vendorData.deleted) {
          vendors.push(vendorData);
        }
      });
      
      // Sort in memory after fetching to avoid index requirement
      if (organizationId) {
        vendors.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      
      return { success: true, data: vendors };
    } catch (error: any) {
      console.error('Error getting all vendors:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single vendor by ID
  getVendor: async (id: string) => {
    try {
      const vendorRef = doc(db, 'vendors', id);
      const docSnap = await getDoc(vendorRef);
      if (docSnap.exists()) {
        const vendorData = { id: docSnap.id, ...docSnap.data() } as Vendor;
        // Don't return deleted vendors
        if (vendorData.deleted) {
          return { success: false, error: 'Vendor not found' };
        }
        return { success: true, data: vendorData };
      } else {
        return { success: false, error: 'Vendor not found' };
      }
    } catch (error: any) {
      console.error('Error getting vendor:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a new vendor
  addVendor: async (vendorData: Omit<Vendor, 'id'>, organizationId?: string) => {
    try {
      const vendorsRef = collection(db, 'vendors');
      // Trim whitespace from name and other string fields before saving
      const trimmedVendorData = {
        ...vendorData,
        name: vendorData.name.trim(),
        ...(vendorData.contact && { contact: vendorData.contact.trim() }),
        ...(vendorData.email && { email: vendorData.email.trim() }),
        ...(vendorData.phone && { phone: vendorData.phone.trim() }),
        ...(vendorData.category && { category: vendorData.category.trim() }),
      };
      const docRef = await addDoc(vendorsRef, {
        ...trimmedVendorData,
        ...(organizationId && { organizationId }), // Add organizationId if provided
        deleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding vendor:', error);
      return { success: false, error: error.message };
    }
  },

  // Update an existing vendor
  updateVendor: async (id: string, vendorData: Partial<Vendor>) => {
    try {
      const vendorRef = doc(db, 'vendors', id);
      // Trim whitespace from name and other string fields if being updated
      const trimmedVendorData: Partial<Vendor> = {
        ...vendorData,
        ...(vendorData.name && { name: vendorData.name.trim() }),
        ...(vendorData.contact && { contact: vendorData.contact.trim() }),
        ...(vendorData.email && { email: vendorData.email.trim() }),
        ...(vendorData.phone && { phone: vendorData.phone.trim() }),
        ...(vendorData.category && { category: vendorData.category.trim() }),
      };
      await updateDoc(vendorRef, {
        ...trimmedVendorData,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      console.error('Error updating vendor:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a vendor (soft delete)
  deleteVendor: async (id: string) => {
    try {
      const vendorRef = doc(db, 'vendors', id);
      await updateDoc(vendorRef, {
        deleted: true,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting vendor:', error);
      return { success: false, error: error.message };
    }
  },

  // Add supplementary document to vendor
  addSupplementaryDocument: async (vendorId: string, document: VendorDocument) => {
    try {
      const vendorRef = doc(db, 'vendors', vendorId);
      const vendorDoc = await getDoc(vendorRef);
      
      if (!vendorDoc.exists()) {
        return { success: false, error: 'Vendor not found' };
      }

      const vendorData = vendorDoc.data() as Vendor;
      const existingDocs = vendorData.supplementaryDocuments || [];
      
      await updateDoc(vendorRef, {
        supplementaryDocuments: [...existingDocs, document],
        updatedAt: serverTimestamp(),
      });
      
      return { success: true };
    } catch (error: any) {
      console.error('Error adding supplementary document:', error);
      return { success: false, error: error.message };
    }
  },

  // Remove supplementary document from vendor
  removeSupplementaryDocument: async (vendorId: string, documentId: string) => {
    try {
      const vendorRef = doc(db, 'vendors', vendorId);
      const vendorDoc = await getDoc(vendorRef);
      
      if (!vendorDoc.exists()) {
        return { success: false, error: 'Vendor not found' };
      }

      const vendorData = vendorDoc.data() as Vendor;
      const existingDocs = vendorData.supplementaryDocuments || [];
      const updatedDocs = existingDocs.filter(doc => doc.id !== documentId);
      
      await updateDoc(vendorRef, {
        supplementaryDocuments: updatedDocs,
        updatedAt: serverTimestamp(),
      });
      
      return { success: true };
    } catch (error: any) {
      console.error('Error removing supplementary document:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload supplementary document file and add to vendor
  uploadSupplementaryDocument: async (
    vendorId: string,
    file: File,
    documentType: VendorDocument['documentType'],
    uploadedBy?: { id: string; email?: string; name?: string }
  ): Promise<{ success: boolean; documentId?: string; error?: string }> => {
    if (!storage) {
      return { success: false, error: 'Firebase Storage not initialized' };
    }

    try {
      // Validate file size
      const { validateFileSize } = await import('@/utils/fileSizeValidation');
      validateFileSize(file);

      // Upload file to storage
      const timestamp = Date.now();
      const sanitizedFileName = sanitizeFileName(file.name);
      const fileName = `vendors/${vendorId}/documents/${timestamp}_${sanitizedFileName}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      // Create document record
      const document: VendorDocument = {
        id: `${timestamp}_${file.name}`,
        fileName: file.name,
        fileUrl: fileUrl,
        documentType: documentType,
        uploadedAt: new Date(),
        uploadedBy: uploadedBy,
        fileSize: file.size,
      };

      // Add to vendor
      const result = await vendorService.addSupplementaryDocument(vendorId, document);
      
      if (result.success) {
        return { success: true, documentId: document.id };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error: any) {
      console.error('Error uploading supplementary document:', error);
      return { success: false, error: error.message };
    }
  },
};

